import { ACTION_TYPES } from '@monorepo/shared/schemas/common'
import { PaginatedCustomersResponseSchema } from '@monorepo/shared/schemas/customers'
import { useQuery } from '@tanstack/react-query'
import {
  Calendar,
  ChevronDown,
  Download,
  Plus,
  RotateCcw,
  X,
} from 'lucide-react'
import { useState } from 'react'
import { get } from '@/lib/api-client'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Calendar as CalendarComponent } from '@/shared/components/ui/calendar'
import { Input } from '@/shared/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import type {
  PropertyFilter,
  SignalFilters as SignalFiltersType,
} from '../types/events.types'
import { SignalCommandSearch } from './SignalCommandSearch'

interface SignalFiltersProps {
  filters: SignalFiltersType
  onFiltersChange: (filters: SignalFiltersType) => void
  onExport?: () => void
  isExporting?: boolean
}

const TIME_RANGE_OPTIONS = [
  { value: 'last_1h', label: 'Last 1 hour' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_24h', label: 'Last 24 hours' },
  { value: 'last_7d', label: 'Last 7 days' },
  { value: 'last_14d', label: 'Last 14 days' },
  { value: 'last_30d', label: 'Last 30 days' },
  { value: 'last_90d', label: 'Last 90 days' },
  { value: 'last_180d', label: 'Last 180 days' },
  { value: 'this_month', label: 'This month' },
  { value: 'year_to_date', label: 'Year to date' },
  { value: 'all_time', label: 'All time' },
  { value: 'custom_last', label: 'In the last...' },
  { value: 'custom_from', label: 'From custom date until now...' },
  { value: 'custom_range', label: 'Custom fixed date range...' },
] as const

const PROPERTY_OPERATIONS = [
  { value: 'equals', label: 'equals' },
  { value: 'not_equals', label: 'not equals' },
  { value: 'contains', label: 'contains' },
  { value: 'greater_than', label: 'greater than' },
  { value: 'less_than', label: 'less than' },
  { value: 'exists', label: 'exists' },
  { value: 'not_exists', label: 'not exists' },
] as const

const SIGNAL_OPTIONS = ACTION_TYPES.map(type => ({
  value: type,
  label: type,
  description: type,
}))

export function SignalFilters({
  filters,
  onFiltersChange,
  onExport,
  isExporting = false,
}: SignalFiltersProps) {
  const [showPropertyFilter, setShowPropertyFilter] = useState(false)
  const [tempPropertyFilters, setTempPropertyFilters] = useState<
    PropertyFilter[]
  >(filters.property_filters || [])
  const [customFromDate, setCustomFromDate] = useState<Date | undefined>()
  const [customToDate, setCustomToDate] = useState<Date | undefined>()
  const [customFromOpen, setCustomFromOpen] = useState(false)
  const [customToOpen, setCustomToOpen] = useState(false)
  const [customLastValue, setCustomLastValue] = useState<number>(1)
  const [customLastUnit, setCustomLastUnit] = useState<
    'hours' | 'days' | 'weeks' | 'months'
  >('hours')

  // Real customer data from API
  const { data: customersData } = useQuery({
    queryKey: ['customers', 'signal-filters', { limit: 50, status: 'active' }],
    queryFn: () =>
      get(
        '/customers?limit=50&status=active',
        PaginatedCustomersResponseSchema
      ),
    staleTime: 5 * 60 * 1000,
  })
  const customerOptions = (customersData?.data ?? []).map(customer => ({
    value: customer.id,
    label: customer.name,
  }))

  // Mock property options for autocomplete
  const propertyOptions = [
    {
      value: 'channel',
      label: 'channel',
      description: 'Communication channel',
    },
    { value: 'user_id', label: 'user_id', description: 'User identifier' },
    {
      value: 'session_id',
      label: 'session_id',
      description: 'Session identifier',
    },
    { value: 'browser', label: 'browser', description: 'Browser type' },
    { value: 'device', label: 'device', description: 'Device type' },
    {
      value: 'location',
      label: 'location',
      description: 'Geographic location',
    },
    { value: 'plan', label: 'plan', description: 'Subscription plan' },
    {
      value: 'company_size',
      label: 'company_size',
      description: 'Company size',
    },
    { value: 'industry', label: 'industry', description: 'Industry type' },
    { value: 'revenue', label: 'revenue', description: 'Revenue amount' },
    {
      value: 'tokens_used',
      label: 'tokens_used',
      description: 'Token consumption',
    },
    {
      value: 'response_time',
      label: 'response_time',
      description: 'Response time in ms',
    },
  ]

  const handleTimeRangeChange = (timeRange: string) => {
    onFiltersChange({
      ...filters,
      time_range: timeRange as SignalFiltersType['time_range'],
      ...(timeRange !== 'custom' && {
        custom_from: undefined,
        custom_to: undefined,
      }),
    })
  }

  const handleSignalNameChange = (signal_name: string) => {
    onFiltersChange({
      ...filters,
      signal_name: signal_name || undefined,
    })
  }

  const handleCustomerChange = (customer_id: string) => {
    onFiltersChange({
      ...filters,
      customer_id: customer_id || undefined,
    })
  }

  const addPropertyFilter = () => {
    setTempPropertyFilters([
      ...tempPropertyFilters,
      { property: '', operation: 'equals', value: '' },
    ])
  }

  const updatePropertyFilter = (
    index: number,
    field: keyof PropertyFilter,
    // biome-ignore lint/suspicious/noExplicitAny: required for dynamic filter values
    value: any
  ) => {
    const updatedFilters = [...tempPropertyFilters]
    updatedFilters[index] = { ...updatedFilters[index], [field]: value }
    setTempPropertyFilters(updatedFilters)
  }

  const removePropertyFilter = (index: number) => {
    setTempPropertyFilters(tempPropertyFilters.filter((_, i) => i !== index))
  }

  const applyPropertyFilters = () => {
    const validFilters = tempPropertyFilters.filter(
      filter =>
        filter.property &&
        filter.operation &&
        (filter.operation === 'exists' ||
          filter.operation === 'not_exists' ||
          filter.value)
    )

    onFiltersChange({
      ...filters,
      property_filters: validFilters.length > 0 ? validFilters : undefined,
    })
    setShowPropertyFilter(false)
  }

  const cancelPropertyFilters = () => {
    setTempPropertyFilters(filters.property_filters || [])
    setShowPropertyFilter(false)
  }

  return (
    <div className='space-y-4'>
      {/* PostHog-style horizontal filter bar */}
      <div className='flex items-center gap-3 py-4 border-b'>
        {/* Time Range Selector - PostHog style */}
        <div className='flex items-center'>
          <Calendar className='w-4 h-4 text-muted-foreground mr-2' />
          <Select
            value={filters.time_range || 'last_1h'}
            onValueChange={handleTimeRangeChange}
          >
            <SelectTrigger className='w-[200px] h-9 border-muted-foreground/30'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIME_RANGE_OPTIONS.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Signal Event Selector - PostHog style */}
        <SignalCommandSearch
          value={filters.signal_name}
          onValueChange={handleSignalNameChange}
          options={SIGNAL_OPTIONS}
          placeholder='Select an event'
          className='w-[200px] h-9 border-muted-foreground/30'
        />

        {/* Customer ID Selector */}
        <SignalCommandSearch
          value={filters.customer_id}
          onValueChange={handleCustomerChange}
          options={customerOptions}
          placeholder='Select customer'
          className='w-[180px] h-9 border-muted-foreground/30'
        />

        {/* Add Filter Button */}
        <Popover open={showPropertyFilter} onOpenChange={setShowPropertyFilter}>
          <PopoverTrigger asChild>
            <Button
              variant='outline'
              size='sm'
              className='h-9 px-3 bg-slate-800 border-slate-700 text-white text-xs hover:bg-slate-700 hover:text-white'
            >
              <Plus className='w-3 h-3 mr-1' />
              Add filter
            </Button>
          </PopoverTrigger>
          <PopoverContent className='w-[600px]' align='start'>
            <div className='space-y-4'>
              <div className='space-y-2'>
                <h4 className='font-medium'>Property Filters</h4>
                <p className='text-sm text-muted-foreground'>
                  Filter events by property values
                </p>
              </div>

              <div className='space-y-3'>
                {tempPropertyFilters.map((filter, index) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                  <div key={index} className='flex items-center gap-2'>
                    <SignalCommandSearch
                      value={filter.property}
                      onValueChange={value =>
                        updatePropertyFilter(index, 'property', value)
                      }
                      options={propertyOptions}
                      placeholder='Select property'
                      className='flex-1'
                    />
                    <Select
                      value={filter.operation}
                      onValueChange={value =>
                        updatePropertyFilter(
                          index,
                          'operation',
                          value as PropertyFilter['operation']
                        )
                      }
                    >
                      <SelectTrigger className='w-32'>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PROPERTY_OPERATIONS.map(op => (
                          <SelectItem key={op.value} value={op.value}>
                            {op.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {filter.operation !== 'exists' &&
                      filter.operation !== 'not_exists' && (
                        <Input
                          placeholder='Value'
                          value={filter.value || ''}
                          onChange={e =>
                            updatePropertyFilter(index, 'value', e.target.value)
                          }
                          className='flex-1'
                        />
                      )}
                    <Button
                      variant='ghost'
                      size='sm'
                      onClick={() => removePropertyFilter(index)}
                      className='h-9 w-9 p-0 hover:bg-red-100 hover:text-red-600'
                    >
                      <X className='w-4 h-4' />
                    </Button>
                  </div>
                ))}

                <Button
                  variant='outline'
                  size='sm'
                  onClick={addPropertyFilter}
                  className='w-full h-8 bg-slate-800 border-slate-700 text-white text-xs hover:bg-slate-700'
                >
                  <Plus className='w-3 h-3 mr-1' />
                  Add filter
                </Button>

                <div className='flex justify-between pt-3 border-t'>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={cancelPropertyFilters}
                  >
                    Cancel
                  </Button>
                  <Button size='sm' onClick={applyPropertyFilters}>
                    Apply
                  </Button>
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {/* Custom Date Range Options */}
        {filters.time_range === 'custom_last' && (
          <div className='flex items-center gap-2 ml-4 pl-4 border-l'>
            <span className='text-sm text-muted-foreground'>In the last</span>
            <Input
              type='number'
              min='1'
              value={customLastValue}
              onChange={e =>
                setCustomLastValue(parseInt(e.target.value, 10) || 1)
              }
              className='w-16 h-9'
            />
            <Select
              value={customLastUnit}
              onValueChange={(value: typeof customLastUnit) =>
                setCustomLastUnit(value)
              }
            >
              <SelectTrigger className='w-20 h-9'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='hours'>hours</SelectItem>
                <SelectItem value='days'>days</SelectItem>
                <SelectItem value='weeks'>weeks</SelectItem>
                <SelectItem value='months'>months</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {filters.time_range === 'custom_from' && (
          <div className='flex items-center gap-2 ml-4 pl-4 border-l'>
            <span className='text-sm text-muted-foreground'>
              From custom date until now:
            </span>
            <Popover open={customFromOpen} onOpenChange={setCustomFromOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant='outline'
                  className='w-44 h-9 justify-between font-normal'
                >
                  {customFromDate
                    ? customFromDate.toLocaleDateString()
                    : 'Select date'}
                  <ChevronDown className='h-4 w-4' />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className='w-auto overflow-hidden p-0'
                align='start'
              >
                <CalendarComponent
                  mode='single'
                  selected={customFromDate}
                  captionLayout='dropdown'
                  onSelect={date => {
                    setCustomFromDate(date)
                    setCustomFromOpen(false)
                    onFiltersChange({
                      ...filters,
                      custom_from: date ? date.toISOString() : undefined,
                    })
                  }}
                />
              </PopoverContent>
            </Popover>
          </div>
        )}

        {filters.time_range === 'custom_range' && (
          <div className='flex items-center gap-2 ml-4 pl-4 border-l'>
            <span className='text-sm text-muted-foreground'>
              Custom fixed date range:
            </span>
            <Popover open={customFromOpen} onOpenChange={setCustomFromOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant='outline'
                  className='w-44 h-9 justify-between font-normal'
                >
                  {customFromDate
                    ? customFromDate.toLocaleDateString()
                    : 'From date'}
                  <ChevronDown className='h-4 w-4' />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className='w-auto overflow-hidden p-0'
                align='start'
              >
                <CalendarComponent
                  mode='single'
                  selected={customFromDate}
                  captionLayout='dropdown'
                  onSelect={date => {
                    setCustomFromDate(date)
                    setCustomFromOpen(false)
                    onFiltersChange({
                      ...filters,
                      custom_from: date ? date.toISOString() : undefined,
                    })
                  }}
                />
              </PopoverContent>
            </Popover>
            <span className='text-muted-foreground text-sm'>to</span>
            <Popover open={customToOpen} onOpenChange={setCustomToOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant='outline'
                  className='w-44 h-9 justify-between font-normal'
                >
                  {customToDate ? customToDate.toLocaleDateString() : 'To date'}
                  <ChevronDown className='h-4 w-4' />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className='w-auto overflow-hidden p-0'
                align='start'
              >
                <CalendarComponent
                  mode='single'
                  selected={customToDate}
                  captionLayout='dropdown'
                  onSelect={date => {
                    setCustomToDate(date)
                    setCustomToOpen(false)
                    onFiltersChange({
                      ...filters,
                      custom_to: date ? date.toISOString() : undefined,
                    })
                  }}
                />
              </PopoverContent>
            </Popover>
          </div>
        )}

        {/* Export Button */}
        {onExport && (
          <div className='ml-auto'>
            <Button
              variant='outline'
              size='sm'
              onClick={onExport}
              disabled={isExporting}
              className='h-9'
            >
              <Download className='w-4 h-4 mr-2' />
              {isExporting ? 'Exporting...' : 'Export'}
            </Button>
          </div>
        )}
      </div>

      {/* Active filters display */}
      {(filters.signal_name ||
        filters.customer_id ||
        filters.time_range !== 'last_1h' ||
        (filters.property_filters && filters.property_filters.length > 0)) && (
        <div className='flex items-center gap-2 text-sm'>
          <span className='text-muted-foreground'>Filters:</span>
          {filters.time_range && filters.time_range !== 'last_1h' && (
            <Badge variant='secondary' className='text-xs'>
              {
                TIME_RANGE_OPTIONS.find(o => o.value === filters.time_range)
                  ?.label
              }
            </Badge>
          )}
          {filters.signal_name && (
            <Badge variant='secondary' className='text-xs'>
              Event: {filters.signal_name}
            </Badge>
          )}
          {filters.customer_id && (
            <Badge variant='secondary' className='text-xs'>
              Customer: {filters.customer_id}
            </Badge>
          )}
          {filters.property_filters?.map((filter, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
            <Badge key={index} variant='secondary' className='text-xs'>
              {filter.property} {filter.operation} {filter.value || ''}
            </Badge>
          ))}
          <Button
            variant='ghost'
            size='sm'
            onClick={() => {
              onFiltersChange({ time_range: 'last_1h' })
              setTempPropertyFilters([])
            }}
            className='h-6 px-2 text-xs text-muted-foreground hover:text-foreground'
          >
            <RotateCcw className='w-3 h-3 mr-1' />
            Clear all
          </Button>
        </div>
      )}
    </div>
  )
}
